import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { toast } from 'react-toastify';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import DayTabBar from './DayTabBar';
import ActivityTimeline from './ActivityTimeline';
import BottomCostBar from './BottomCostBar';
import TripInfoCards from './TripInfoCards';
import ItineraryMap from '../ItineraryMap';
import { computeDayVibe } from '../../utils/vibeColors';
import ActivityCardCarousel, { CAROUSEL_HEIGHT, gatherActivities, DAY_COLORS } from './ActivityCardCarousel';
import PlaceDetailSheet from './PlaceDetailSheet';
import { RatingButton } from '../feedback/RatingButton';
import { ArrowLeftIcon, ArrowDownTrayIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import ShareMenu from '../share/ShareMenu';
import NudgeInput from './NudgeInput';
import { itineraryEditApi, imagesApi } from '../../services/api';

/* ─── Mobile detection hook ─── */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isMobile;
};

/* ═══════════════════════════════════════════
   STICKY HEADER (shared mobile + desktop)
   ═══════════════════════════════════════════ */

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  gap: 10px;
  min-height: 52px;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(255, 255, 255, 0.96);
  }
`;

const HeaderBackBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  padding: 8px;
  flex-shrink: 0;

  &:active {
    transform: scale(0.92);
  }
`;

const HeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const HeaderIconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  padding: 8px;

  &:active {
    transform: scale(0.92);
  }
`;

/* ─── More menu (header overflow) ─── */
const MoreMenuWrapper = styled.div`
  position: relative;
`;

const MoreMenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 8px;
  min-width: 190px;
  z-index: 200;
`;

const MoreMenuLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 4px 10px 6px 10px;
`;

const MoreMenuDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.06);
  margin: 4px 0;
`;

const MoreMenuRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  text-align: left;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  svg {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
    color: #6b7280;
  }
`;

/* ─── Confidence Indicator Bar ─── */
const ConfidenceBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 16px;
  background: rgba(66, 133, 244, 0.06);
  border-bottom: 1px solid rgba(66, 133, 244, 0.1);
  font-size: 11px;
  color: #6b7280;

  svg {
    width: 13px;
    height: 13px;
    color: #4285F4;
    flex-shrink: 0;
  }
`;

const ConfidencePercent = styled.span`
  font-weight: 600;
  color: #1A73E8;
`;

/* ─── Edit Mode Toggle (hidden for now) ─── */
// const EditToggle = styled.button`
//   display: flex;
//   align-items: center;
//   gap: 6px;
//   padding: 6px 14px;
//   border-radius: 9999px;
//   border: 1.5px solid ${props => props.$active ? '#4285F4' : 'rgba(0, 0, 0, 0.12)'};
//   background: ${props => props.$active ? 'rgba(66, 133, 244, 0.08)' : 'transparent'};
//   color: ${props => props.$active ? '#4285F4' : '#6b7280'};
//   font-size: 12px;
//   font-weight: 600;
//   cursor: pointer;
//   transition: all 0.2s;
//
//   &:hover {
//     border-color: #4285F4;
//     color: #4285F4;
//   }
//
//   svg {
//     width: 14px;
//     height: 14px;
//   }
// `;

/* ─── Satisfaction Survey ─── */
const SurveyBanner = styled(motion.div)`
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 420px;
  width: calc(100% - 32px);
`;

const SurveyText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
`;

const SurveyButtons = styled.div`
  display: flex;
  gap: 6px;
`;

const SurveyBtn = styled.button`
  background: none;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

/* ═══════════════════════════════════════════
   OUTER WRAPPER (holds sticky header + content)
   ═══════════════════════════════════════════ */

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`;

/* ═══════════════════════════════════════════
   DESKTOP STYLES
   ═══════════════════════════════════════════ */

const ScenicBackground = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-image: url(${props => props.$bgImage || GlassmorphismTokens.scenicBackground});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  overflow: hidden;
`;

const GlassCard = styled.div`
  width: 90%;
  height: 100%;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 2.5rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;

  @supports not (backdrop-filter: blur(1px)) {
    background: rgba(255, 255, 255, 0.92);
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  padding: 16px 32px 24px 32px;
  gap: 32px;
  border-radius: 0 0 2.5rem 2.5rem;
`;

const TimelinePanel = styled.div`
  width: 50%;
  overflow-y: auto;
  padding-right: 16px;
  position: relative;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
  }
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.1) transparent;
`;

const MapPanel = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MapFrame = styled.div`
  flex: 1;
  min-height: 0;
  border-radius: 2rem;
  overflow: hidden;
  border: 4px solid white;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
  position: relative;
`;

/* ═══════════════════════════════════════════
   MOBILE STYLES
   ═══════════════════════════════════════════ */

const MobileContent = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`;

const MobileMapBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

/* ═══════════════════════════════════════════
   SHARE / DOWNLOAD HELPERS
   ═══════════════════════════════════════════ */

const doShare = async (itineraryData, extractedCity) => {
  const title = itineraryData.title || `Trip to ${itineraryData.destination || extractedCity || 'somewhere amazing'}`;
  const url = window.location.href;

  if (navigator.share) {
    try {
      await navigator.share({ title, url });
    } catch (err) { /* cancelled */ }
  } else {
    try {
      await navigator.clipboard.writeText(url);
      const el = document.createElement('div');
      el.textContent = 'Link copied!';
      Object.assign(el.style, {
        position: 'fixed', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
        background: '#1f2937', color: 'white', padding: '8px 20px', borderRadius: '20px',
        fontSize: '13px', fontWeight: '600', zIndex: '9999', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      });
      document.body.appendChild(el);
      setTimeout(() => el.remove(), 2000);
    } catch (err) { /* no clipboard */ }
  }
};

const doDownload = (itineraryData) => {
  const dailyItinerary = itineraryData.dailyItinerary || itineraryData.dayByDay || [];
  const title = itineraryData.title || itineraryData.destination || 'Itinerary';

  let text = `${title}\n${'='.repeat(title.length)}\n\n`;

  if (itineraryData.description || itineraryData.tripSummary) {
    text += `${itineraryData.description || itineraryData.tripSummary}\n\n`;
  }

  dailyItinerary.forEach((day, idx) => {
    const dayNum = day.day || idx + 1;
    text += `--- Day ${dayNum} ${day.title ? '- ' + day.title : ''} ---\n`;

    ['morning', 'afternoon', 'evening'].forEach(block => {
      const activities = day[block];
      if (activities && activities.length > 0) {
        text += `\n  ${block.charAt(0).toUpperCase() + block.slice(1)}:\n`;
        activities.forEach((a) => {
          const name = typeof a === 'string' ? a : (a.activity || '');
          text += `    - ${name}\n`;
          if (a.description) text += `      ${a.description}\n`;
        });
      }
    });
    text += '\n';
  });

  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.txt`;
  a.click();
  URL.revokeObjectURL(url);
};

/* ═══════════════════════════════════════════ */

const ItinerarySplitView = ({
  itineraryData,
  mapRef,
  userId,
  isAuthenticated,
  fetchedItinerary,
  onActivityClick,
  onBookAll,
  extractedCity,
  onCityExtracted,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDay, setActiveDay] = useState(0);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [localItinerary, setLocalItinerary] = useState(itineraryData);
  const [editVersion, setEditVersion] = useState(0);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [photoCache, setPhotoCache] = useState({});
  const [selectedDesktopActivity, setSelectedDesktopActivity] = useState(null);

  const [surveyState, setSurveyState] = useState('hidden'); // hidden | showing | dismissed
  const isMobile = useIsMobile();

  // Keep localItinerary in sync when itineraryData prop changes (e.g., page navigation)
  useEffect(() => {
    setLocalItinerary(itineraryData);
  }, [itineraryData]);
  const mobileContentRef = useRef(null);
  const moreMenuRef = useRef(null);

  useEffect(() => {
    if (!showMoreMenu) return;
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreMenu]);

  // Use localItinerary in edit mode (reflects edits), itineraryData otherwise
  const effectiveData = itineraryData;
  const dailyItinerary = React.useMemo(
    () => effectiveData.dailyItinerary || effectiveData.dayByDay || [],
    [effectiveData]
  );
  const currentDay = dailyItinerary[activeDay];

  // Compute vibe colors per day for DayTabBar dots
  const vibeColors = React.useMemo(() =>
    dailyItinerary.map(day => {
      const allActivities = [
        ...(day.morning || []),
        ...(day.afternoon || []),
        ...(day.evening || []),
        ...(day.activities || []),
      ];
      return computeDayVibe(allActivities);
    }),
    [dailyItinerary]
  );


  // Pre-fetch Unsplash thumbnail for every card immediately on mount.
  // Mobile: uses gatherActivities IDs (day{dayNum}-block-idx) for the carousel.
  // Uses Unsplash (not Google Places) so there's zero dependency on Maps JS load timing.
  useEffect(() => {
    if (!isMobile || !dailyItinerary.length) return;
    let cancelled = false;
    const activities = gatherActivities(dailyItinerary);
    const timers = [];

    activities.forEach((act, i) => {
      timers.push(setTimeout(async () => {
        if (cancelled) return;
        try {
          const q = act.place || act.name;
          const res = await imagesApi.searchImages(q, 1, 'unsplash');
          if (cancelled) return;
          const img = (res.data || []).find(x => x.imageUrl && x.mimeType?.startsWith('image/'));
          if (img) setPhotoCache(prev => ({ ...prev, [act.id]: img.thumbnailUrl || img.imageUrl }));
        } catch { /* ignore */ }
      }, i * 300));
    });

    return () => { cancelled = true; timers.forEach(clearTimeout); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, dailyItinerary]);

  // Desktop: pre-fetch Unsplash thumbnails using ActivityTimeline's ID format (day{dayIndex}-block-idx).
  useEffect(() => {
    if (isMobile || !dailyItinerary.length) return;
    let cancelled = false;
    const tasks = [];

    dailyItinerary.forEach((day, dayIndex) => {
      ['morning', 'afternoon', 'evening'].forEach(block => {
        const acts = day[block] || [];
        acts.forEach((act, actIdx) => {
          const name = (typeof act === 'object' && act)
            ? (act.place || act.activity || act.name || '')
            : (typeof act === 'string' ? act : '');
          if (!name) return;
          tasks.push({ id: `day${dayIndex}-${block}-${actIdx}`, name });
        });
      });
    });

    const timers = tasks.map((task, i) =>
      setTimeout(async () => {
        if (cancelled) return;
        try {
          const res = await imagesApi.searchImages(task.name, 1, 'unsplash');
          if (cancelled) return;
          const img = (res.data || []).find(x => x.imageUrl && x.mimeType?.startsWith('image/'));
          if (img) setPhotoCache(prev => ({ ...prev, [task.id]: img.thumbnailUrl || img.imageUrl }));
        } catch { /* ignore */ }
      }, i * 300)
    );

    return () => { cancelled = true; timers.forEach(clearTimeout); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, dailyItinerary]);

  const bgImage = itineraryData.heroImage || itineraryData.image || itineraryData.coverImage || null;

  // Desktop parallax: subtle background shift on scroll
  const pageWrapperRef = useRef(null);
  const { scrollY } = useScroll({ container: pageWrapperRef });
  const parallaxY = useTransform(scrollY, [0, 400], [0, -40]);

  const rawCost = itineraryData.estimatedCost || itineraryData.estimatedBudget;
  const budgetLabel = React.useMemo(() => {
    if (!rawCost) return null;
    if (typeof rawCost === 'string') return rawCost;
    if (typeof rawCost !== 'number' || Number.isNaN(rawCost)) return null;
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: itineraryData.currency || 'USD',
        maximumFractionDigits: 0,
      }).format(rawCost);
    } catch {
      return `$${rawCost.toLocaleString()}`;
    }
  }, [rawCost, itineraryData.currency]);

  const titleText = itineraryData.title || itineraryData.destination || extractedCity || '';
const itineraryId = itineraryData._id || itineraryData.id || fetchedItinerary?._id;

  const handleActivityClick = useCallback((activity, dayIndex, activityIndex, activityId) => {
    setSelectedActivityId(activityId);

    const dn = currentDay?.day || activeDay + 1;
    const timeBlock = activityId?.split('-')[1];
    const actIdx = activityId?.split('-')[2];

    const timeMap = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };
    const mapTime = timeMap[timeBlock] || timeBlock;
    const mapLocationId = `day${dn}-${mapTime}-${actIdx}`;

    setActiveLocationId(mapLocationId);

    // Open Google Places detail panel on desktop
    const actObj = typeof activity === 'object' && activity ? activity : {};
    setSelectedDesktopActivity({
      id: activityId,
      name: actObj.activity || actObj.name || (typeof activity === 'string' ? activity : ''),
      description: actObj.description || '',
      time: actObj.time || '',
      block: timeBlock,
      duration: actObj.duration,
      dayNum: dn,
      dayIdx: dayIndex,
      color: DAY_COLORS[dayIndex % DAY_COLORS.length],
      latitude: actObj.latitude,
      longitude: actObj.longitude,
      place: actObj.place,
    });

    if (onActivityClick) {
      onActivityClick(activity, dayIndex, activityIndex);
    }
  }, [activeDay, currentDay, onActivityClick]);

  const handleMarkerSelect = useCallback((location) => {
    setActiveLocationId(location.id);

    // On desktop, open the Places detail panel for the clicked pin
    if (!isMobile) {
      // location.id = "day{dayNum}-{Morning|Afternoon|Evening}-{actIdx}"
      const parts = location.id.split('-');
      const timeBlock = (parts[1] || '').toLowerCase();
      const actIdx = parseInt(parts[2] || '0', 10);
      const activityId = `day${location.dayIndex}-${timeBlock}-${actIdx}`;
      setSelectedActivityId(activityId);

      const actObj = dailyItinerary[location.dayIndex]?.[timeBlock]?.[actIdx];
      setSelectedDesktopActivity({
        id: activityId,
        name: location.name,
        description: (typeof actObj === 'object' ? actObj?.description : '') || location.description || '',
        time: location.time || '',
        block: timeBlock,
        duration: location.duration,
        dayNum: location.day,
        dayIdx: location.dayIndex,
        color: DAY_COLORS[location.dayIndex % DAY_COLORS.length],
        latitude: location.position?.lat,
        longitude: location.position?.lng,
        place: location.name,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, dailyItinerary]);

  const handleDayChange = useCallback((day) => {
    setActiveDay(day);
    setSelectedActivityId(null);
    setActiveLocationId(null);
    setSelectedDesktopActivity(null);
  }, []);


  // Helper: find activity index by name (robust against stale references)
  const findActivityIndex = useCallback((dayIdx, timeBlock, activity) => {
    const activities = localItinerary.dailyItinerary?.[dayIdx]?.[timeBlock] || [];
    const name = typeof activity === 'string' ? activity : activity?.activity;
    if (!name) return activities.indexOf(activity); // fallback to ref equality
    return activities.findIndex(a =>
      (typeof a === 'string' ? a : a?.activity) === name
    );
  }, [localItinerary]);

  // Helper: call edit API and update local state
  const callEditApi = useCallback(async (action, dayIdx, timeBlock, activityIndex, parameters) => {
    try {
      const response = await itineraryEditApi.editItinerary(itineraryId, userId, {
        action,
        dayIndex: dayIdx,
        timeBlock,
        activityIndex,
        parameters,
      });
      setLocalItinerary(response.data);
      setEditVersion(v => v + 1); // Force re-render of timeline components
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to update itinerary';
      toast.error(msg);
      throw err;
    }
  }, [itineraryId, userId]);


  // Satisfaction survey — show after 30s on a new itinerary
  useEffect(() => {
    if (!itineraryId) return;
    const key = `survey_${itineraryId}`;
    if (localStorage.getItem(key)) return;
    const timer = setTimeout(() => {
      setSurveyState('showing');
    }, 30000);
    return () => clearTimeout(timer);
  }, [itineraryId]);

  const handleSurveyResponse = useCallback((response) => {
    if (!itineraryId) return;
    localStorage.setItem(`survey_${itineraryId}`, 'true');
    if (response === 'perfect') {
      toast.success('Glad you love it!');
      setSurveyState('dismissed');
    } else if (response === 'almost') {
      setSurveyState('nudge');
    } else {
      setSurveyState('dismissed');
      toast.info('You can regenerate from the home page anytime.');
    }
  }, [itineraryId]);

  // NudgeInput update handler
  const handleNudgeUpdate = useCallback((data) => {
    if (data) {
      setLocalItinerary(data);
      setEditVersion(v => v + 1);
      toast.success('Itinerary updated!');
    }
    setSurveyState('dismissed');
  }, []);

  // Store pre-nudge state for undo
  const preNudgeRef = useRef(null);

  const undoNudge = useCallback((snapshot) => {
    setLocalItinerary(snapshot);
    setEditVersion(v => v + 1);
    preNudgeRef.current = null;
    toast.dismiss();
    toast.info('Change undone');
  }, []);

  const handleMoreLikeThis = useCallback(async (dayIdx, timeBlock, activity) => {
    const activityIndex = findActivityIndex(dayIdx, timeBlock, activity);
    if (activityIndex === -1) { toast.error('Activity not found'); return; }
    const snapshot = JSON.parse(JSON.stringify(localItinerary));
    try {
      await callEditApi('SWAP_SIMILAR', dayIdx, timeBlock, activityIndex, null);
      preNudgeRef.current = snapshot;
      toast('Swapped with similar activity', {
        autoClose: 5000,
        closeButton: ({ closeToast }) => (
          <button onClick={() => { undoNudge(snapshot); closeToast(); }}
            style={{ background: 'none', border: 'none', color: '#4285F4', fontWeight: 600, cursor: 'pointer', padding: '4px 8px', fontSize: 13 }}>
            Undo
          </button>
        ),
      });
    } catch { /* error toast already shown */ }
  }, [callEditApi, findActivityIndex, localItinerary, undoNudge]);


  const handleRegenerateDay = useCallback(async (dayIdx, vibe) => {
    const snapshot = JSON.parse(JSON.stringify(localItinerary));
    try {
      await callEditApi('REGENERATE_DAY', dayIdx, null, null, vibe ? { theme: vibe } : null);
      preNudgeRef.current = snapshot;
      toast('Day regenerated', {
        autoClose: 5000,
        closeButton: ({ closeToast }) => (
          <button onClick={() => { undoNudge(snapshot); closeToast(); }}
            style={{ background: 'none', border: 'none', color: '#4285F4', fontWeight: 600, cursor: 'pointer', padding: '4px 8px', fontSize: 13 }}>
            Undo
          </button>
        ),
      });
    } catch { /* error toast already shown */ }
  }, [callEditApi, localItinerary, undoNudge]);

  // Compute confidence stats from activities
  const confidenceStats = React.useMemo(() => {
    let verified = 0;
    let aiSuggested = 0;
    let total = 0;

    dailyItinerary.forEach((day) => {
      ['morning', 'afternoon', 'evening'].forEach((block) => {
        const activities = day[block] || [];
        activities.forEach((a) => {
          if (typeof a === 'string') { total++; aiSuggested++; return; }
          total++;
          if (a.lastVerifiedAt || a.last_verified_at || a.rating || a.averageRating || a.popularityScore || a.source === 'internal_db') {
            verified++;
          } else {
            aiSuggested++;
          }
        });
      });
      // Also check activities array
      if (day.activities && Array.isArray(day.activities)) {
        day.activities.forEach((a) => {
          if (typeof a === 'string') { total++; aiSuggested++; return; }
          total++;
          if (a.lastVerifiedAt || a.last_verified_at || a.rating || a.averageRating || a.popularityScore || a.source === 'internal_db') {
            verified++;
          } else {
            aiSuggested++;
          }
        });
      }
    });

    const percent = total > 0 ? Math.round((verified / total) * 100) : 0;
    return { verified, aiSuggested, total, percent };
  }, [dailyItinerary]);

  // Hidden Gem Score — percentage of activities with local/hidden/offbeat keywords
  const hiddenGemScore = React.useMemo(() => {
    const keywords = /local|hidden|offbeat|secret|underrated|lesser.known|authentic|undiscovered/i;
    let total = 0;
    let gems = 0;
    dailyItinerary.forEach((day) => {
      ['morning', 'afternoon', 'evening', 'activities'].forEach((block) => {
        const activities = day[block];
        if (!Array.isArray(activities)) return;
        activities.forEach((a) => {
          total++;
          const text = typeof a === 'string'
            ? a
            : [a.activity, a.name, a.description, a.type, a.category].filter(Boolean).join(' ');
          if (keywords.test(text)) gems++;
        });
      });
    });
    return total > 0 ? Math.round((gems / total) * 100) : 0;
  }, [dailyItinerary]);

  if (!dailyItinerary.length) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
        No itinerary data available
      </div>
    );
  }

  /* ─── Shared sticky header ─── */
  const stickyHeader = (
    <>
      <StickyHeader>
        <HeaderBackBtn onClick={() => location.key === 'default' ? navigate('/') : navigate(-1)} aria-label="Back">
          <ArrowLeftIcon style={{ width: '18px', height: '18px', color: '#374151' }} />
        </HeaderBackBtn>

        <HeaderTitle title={titleText}>
          {titleText || 'Itinerary'}
        </HeaderTitle>

        {hiddenGemScore > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            padding: '3px 10px', borderRadius: '9999px', flexShrink: 0,
            background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)',
            color: '#059669', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
          }}>
            {hiddenGemScore}% Hidden Gems {'\u{1F48E}'}
          </span>
        )}

        {budgetLabel && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '3px 10px', borderRadius: '9999px', flexShrink: 0,
            background: 'rgba(37, 99, 235, 0.08)', border: '1px solid rgba(37, 99, 235, 0.2)',
            color: '#2563eb', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
          }}>
            {budgetLabel}
          </span>
        )}

        <HeaderActions>
          <ShareMenu
            itineraryData={itineraryData}
            extractedCity={extractedCity}
            onShareLink={() => doShare(itineraryData, extractedCity)}
            onDownloadText={() => doDownload(itineraryData)}
          />
          <MoreMenuWrapper ref={moreMenuRef}>
            <HeaderIconBtn
              onClick={() => setShowMoreMenu(prev => !prev)}
              aria-label="More options"
              title="More"
            >
              <EllipsisHorizontalIcon style={{ width: '22px', height: '22px', color: '#374151' }} />
            </HeaderIconBtn>
            {showMoreMenu && (
              <MoreMenuDropdown>
                {isAuthenticated && itineraryId && (
                  <>
                    <MoreMenuLabel>Rate this trip</MoreMenuLabel>
                    <div style={{ padding: '2px 10px 8px 10px' }}>
                      <RatingButton itineraryId={itineraryId} userId={userId} compact />
                    </div>
                    <MoreMenuDivider />
                  </>
                )}
                <MoreMenuRow
                  onClick={() => { doDownload(itineraryData); setShowMoreMenu(false); }}
                >
                  <ArrowDownTrayIcon />
                  Download itinerary
                </MoreMenuRow>
              </MoreMenuDropdown>
            )}
          </MoreMenuWrapper>
        </HeaderActions>
      </StickyHeader>
      {confidenceStats.percent > 70 && (
        <ConfidenceBanner>
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-2.546-4.46a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
          </svg>
          <ConfidencePercent>{confidenceStats.percent}%</ConfidencePercent> verified
          · {confidenceStats.aiSuggested} suggested
        </ConfidenceBanner>
      )}
    </>
  );

  /* ─── MOBILE LAYOUT ─── */
  if (isMobile) {
    return (
      <PageWrapper>
        {stickyHeader}

        <MobileContent ref={mobileContentRef}>
          <MobileMapBackground>
            <ItineraryMap
              ref={mapRef}
              itineraryData={itineraryData}
              googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              fillContainer
              onCityExtracted={onCityExtracted}
              activeLocationId={activeLocationId}
              onMarkerSelect={(loc) => {
                handleMarkerSelect(loc);
                // Build activity object directly from the marker location
                // (loc.id = "day{dayNum}-{Morning|Afternoon|Evening}-{actIdx}")
                const parts = loc.id.split('-');
                const timeBlock = (parts[1] || '').toLowerCase();
                const actIdx = parseInt(parts[2] || '0', 10);
                const actObj = dailyItinerary[loc.dayIndex]?.[timeBlock]?.[actIdx];
                setSelectedCard({
                  id: `day${loc.day}-${timeBlock}-${actIdx}`,
                  name: loc.name,
                  description: (typeof actObj === 'object' ? actObj?.description : '') || loc.description || '',
                  block: timeBlock,
                  dayNum: loc.day,
                  dayIdx: loc.dayIndex,
                  color: DAY_COLORS[loc.dayIndex % DAY_COLORS.length],
                  latitude: loc.position?.lat,
                  longitude: loc.position?.lng,
                  place: loc.name,
                });
              }}
              activeDayIndex={activeDay}
              overlayBottomOffset={CAROUSEL_HEIGHT}
              isMapExpanded={false}
            />
          </MobileMapBackground>

          <TripInfoCards itineraryData={itineraryData} isMobile sheetHeight={CAROUSEL_HEIGHT} />

          {/* Bottom card carousel */}
          <ActivityCardCarousel
            dailyItinerary={dailyItinerary}
            selectedCardId={selectedCard?.id}
            photoCache={photoCache}
            onCardTap={(act) => {
              setSelectedCard(act);
              // Pan map to this activity's location
              const mapTime = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' }[act.block] || act.block;
              setActiveLocationId(`day${act.dayNum}-${mapTime}-${act.id.split('-')[2]}`);
              handleDayChange(act.dayIdx);
            }}
          />

          {/* Place detail sheet */}
          {selectedCard && (
            <PlaceDetailSheet
              activity={selectedCard}
              onClose={() => setSelectedCard(null)}
              onFirstPhotoLoaded={(id, url) =>
                setPhotoCache(prev => ({ ...prev, [id]: url }))
              }
            />
          )}
        </MobileContent>

        <BottomCostBar
          estimatedCost={itineraryData.estimatedCost || itineraryData.estimatedBudget}
          currency={itineraryData.currency}
          onBookAll={onBookAll}
          isMobile
        />


        {surveyState === 'nudge' && (
          <NudgeInput itineraryId={itineraryId} userId={userId} onUpdate={handleNudgeUpdate} />
        )}

        {/* Satisfaction Survey */}
        <AnimatePresence>
          {surveyState === 'showing' && (
            <SurveyBanner
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3 }}
            >
              <SurveyText>How does this itinerary feel?</SurveyText>
              <SurveyButtons>
                <SurveyBtn onClick={() => handleSurveyResponse('perfect')}>Perfect</SurveyBtn>
                <SurveyBtn onClick={() => handleSurveyResponse('almost')}>Almost</SurveyBtn>
                <SurveyBtn onClick={() => handleSurveyResponse('off')}>Off</SurveyBtn>
              </SurveyButtons>
            </SurveyBanner>
          )}
        </AnimatePresence>
      </PageWrapper>
    );
  }

  /* ─── DESKTOP LAYOUT ─── */
  return (
    <PageWrapper>
      {stickyHeader}

      <ScenicBackground as={motion.div} $bgImage={bgImage} style={!isMobile ? { backgroundPositionY: parallaxY } : undefined}>
        <GlassCard>
          <DayTabBar
            days={dailyItinerary}
            activeDay={activeDay}
            onDayChange={handleDayChange}
            vibeColors={vibeColors}
          />

          <ContentRow>
            <TimelinePanel>
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${activeDay}-${editVersion}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <ActivityTimeline
                      day={currentDay}
                      dayIndex={activeDay}
                      onActivityClick={handleActivityClick}
                      selectedActivityId={selectedActivityId}
                      photoCache={photoCache}
                      onMoreLikeThis={handleMoreLikeThis}
                      onSomethingDifferent={handleRegenerateDay}
                      isOwnItinerary={itineraryData.userId === userId}
                    />
                </motion.div>
              </AnimatePresence>
            </TimelinePanel>

            <MapPanel>
              <MapFrame>
                <ItineraryMap
                  ref={mapRef}
                  itineraryData={itineraryData}
                  googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                  fillContainer
                  onCityExtracted={onCityExtracted}
                  activeLocationId={activeLocationId}
                  onMarkerSelect={handleMarkerSelect}
                  activeDayIndex={activeDay}
                  panLeftOffset={selectedDesktopActivity ? 380 : 0}
                />
                <TripInfoCards itineraryData={itineraryData} />
                {selectedDesktopActivity && (
                  <PlaceDetailSheet
                    key={selectedDesktopActivity.id}
                    activity={selectedDesktopActivity}
                    variant="desktop"
                    onClose={() => setSelectedDesktopActivity(null)}
                    onFirstPhotoLoaded={null}
                  />
                )}
              </MapFrame>
              <BottomCostBar
                estimatedCost={itineraryData.estimatedCost || itineraryData.estimatedBudget}
                currency={itineraryData.currency}
                onBookAll={onBookAll}
              />
            </MapPanel>
          </ContentRow>
          {(surveyState === 'nudge') && (
            <NudgeInput itineraryId={itineraryId} userId={userId} onUpdate={handleNudgeUpdate} />
          )}
        </GlassCard>
      </ScenicBackground>

      {/* Satisfaction Survey */}
      <AnimatePresence>
        {surveyState === 'showing' && (
          <SurveyBanner
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3 }}
          >
            <SurveyText>How does this itinerary feel?</SurveyText>
            <SurveyButtons>
              <SurveyBtn onClick={() => handleSurveyResponse('perfect')}>Perfect</SurveyBtn>
              <SurveyBtn onClick={() => handleSurveyResponse('almost')}>Almost</SurveyBtn>
              <SurveyBtn onClick={() => handleSurveyResponse('off')}>Off</SurveyBtn>
            </SurveyButtons>
          </SurveyBanner>
        )}
      </AnimatePresence>
    </PageWrapper>
  );
};

export default ItinerarySplitView;
